import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { isNil } from 'lodash';
import { catchError, EMPTY, Observable, switchMap } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  ESqlOperators,
  ICurrentAndPreviousShifts,
  IFormSubmissionConfiguration,
  ILine,
  IOeeFormAssociationDetails,
  ISelection,
  ITypeBasedSelectionDetails,
  WIDGET_GLOBAL_DATA,
  widgetHttpHeader,
} from 'scw-digital-boards-widgets';
import { BaseDatatableStore, IBaseDatatableState } from '../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { IEnhancedFormSubmission, IFormSubmissionData } from './form-submissions.interface';

export interface IFormSubmissionsComponentState extends IBaseDatatableState<IFormSubmissionData> {
  bulkCrudLoading: ELoadStatus;
  bulkOperationFailedData: IBaseCrudResponse[];
  data: IFormSubmissionData[];
  dataLoading: ELoadStatus;
  dataTotal: number;
  singleCrudLoading: ELoadStatus;
  currentPreviousShiftsForFilter: number[];
  loadFilterData: ELoadStatus;
  errorStatus: number | null;
}

@Injectable()
export class FormSubmissionsStore extends BaseDatatableStore<IFormSubmissionData, IFormSubmissionsComponentState> {
  private readonly oeeTrackerHttpContext: HttpContext = inject(WIDGET_GLOBAL_DATA).oeeTrackerHttpContext;

  readonly currentPreviousShiftsForFilter$: Observable<number[]> = this.select(
    (state) => state.currentPreviousShiftsForFilter,
  );
  readonly loadFilterData$: Observable<ELoadStatus> = this.select((state) => state.loadFilterData);

  readonly loadFormSubmissions = this.effect<{
    params: IGenericCrudRequestConstructionParameters;
    additionalSearch?: string;
  }>((trigger$) =>
    trigger$.pipe(
      switchMap(({ params, additionalSearch }) => {
        let httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        if (additionalSearch) {
          httpParams = httpParams.append('additionalSearch', additionalSearch);
        }

        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getFormSubmissions(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IEnhancedFormSubmission>) =>
              this.patchState({
                data: this.mapFormSubmissions(response.data),
                dataLoading: ELoadStatus.success,
                dataTotal: response.total,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  public readonly loadCurrentShiftsOfLines = this.effect<{
    includeCurrent: boolean;
    includePrevious: boolean;
    lineIds: number[];
  }>((trigger$) => {
    return trigger$.pipe(
      filter(({ lineIds }) => lineIds && lineIds.length > 0),
      switchMap(({ includeCurrent, includePrevious, lineIds }) => {
        this.patchState({
          errorStatus: null,
          loadFilterData: ELoadStatus.loading,
        });

        const params: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters({
          fields: ['id', 'title', 'siteId'],
          filters: [{ field: 'id', ids: lineIds, operator: ESqlOperators.$in }],
        });

        return this.getLines({ headers: widgetHttpHeader, params }).pipe(
          switchMap((lines) => {
            const siteIds: number[] = lines.data.reduce((acc: number[], line) => {
              if (line.siteId) acc.push(line.siteId);

              return acc;
            }, []);

            return this.getCurrentAndPreviousShiftsOfLines({
              headers: widgetHttpHeader,
              params: new HttpParams().set('lineIds', JSON.stringify(lineIds)).set('siteIds', JSON.stringify(siteIds)),
            });
          }),
          tapResponse(
            (response) => {
              const currentPreviousShiftIds: number[] = [];

              if (response?.data?.length > 0) {
                for (const shiftItem of response.data) {
                  if (includeCurrent && shiftItem.currentShift) {
                    currentPreviousShiftIds.push(Number(shiftItem.currentShift.shiftId));
                  }

                  if (includePrevious && shiftItem.previousShift) {
                    currentPreviousShiftIds.push(Number(shiftItem.previousShift.shiftId));
                  }
                }
              }

              this.patchState({
                currentPreviousShiftsForFilter: currentPreviousShiftIds,
                loadFilterData: ELoadStatus.success,
              });
            },
            (error: HttpErrorResponse) => {
              this.patchState({ errorStatus: error.status, loadFilterData: ELoadStatus.failure });
            },
          ),
        );
      }),
      catchError((error: HttpErrorResponse) => {
        this.patchState({ errorStatus: error.status, loadFilterData: ELoadStatus.failure });

        return EMPTY;
      }),
    );
  });

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
    private readonly translate: TranslateService,
  ) {
    super({
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      currentPreviousShiftsForFilter: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      errorStatus: null,
      loadFilterData: ELoadStatus.initial,
      singleCrudLoading: ELoadStatus.initial,
    });
  }

  private mapFormSubmissions(data: IEnhancedFormSubmission[]): IFormSubmissionData[] {
    return data.map((submission: IEnhancedFormSubmission) => ({
      dashboard: submission.dashboard?.name ?? null,
      form: submission.form?.name ?? null,
      formAssociation: this.parseFormAssociation(submission.configuration),
      formioForm: submission.form?.form ?? null,
      id: submission.id,
      isDashboardDeleted: !!submission.dashboard?.deletedAt,
      isFormDeleted: !!submission.form?.deletedAt,
      submission: submission.submission,
      submittedAt: submission.createdAt ?? null,
      submittedBy: submission.user?.name ?? null,
    }));
  }

  private parseFormAssociation(association: IFormSubmissionConfiguration): string | null {
    const oee: IOeeFormAssociationDetails | undefined = association?.oeeFormAssociation;

    if (!oee) {
      return null;
    }

    const formattedLines: string[] = [];
    const dynamicSources: ITypeBasedSelectionDetails[] = [oee.currentShift, oee.currentWorkOrder];
    const staticSources: { data: ISelection[]; type: string }[] = [
      { data: oee.line, type: 'line' },
      { data: oee.shift, type: 'shift' },
      { data: oee.workOrder, type: 'workOrder' },
    ];

    dynamicSources.forEach((source) => {
      if (source?.selections?.length > 0) {
        const label: string = this.translate.instant(`widget.formIo.formAssociationOptions.${source.selectedType}`);
        const value: string = source.selections.map((s: ISelection) => s.name).join(', ');
        formattedLines.push(`- ${label}: ${value}`);
      }
    });

    staticSources.forEach((source) => {
      if (source.data?.length > 0) {
        const label: string = this.translate.instant(`widget.formIo.formAssociationOptions.${source.type}`);
        const value: string = source.data.map((s: ISelection) => s.name).join(', ');
        const newLine = `- ${label}: ${value}`;

        if (!formattedLines.includes(newLine)) {
          formattedLines.push(newLine);
        }
      }
    });

    return formattedLines.length > 0 ? formattedLines.join('\n') : null;
  }

  private getFormSubmissions(params: HttpParams): Observable<IGetManyResponse<IEnhancedFormSubmission>> {
    return this.http.get<IGetManyResponse<IEnhancedFormSubmission>>('form-submissions', { params });
  }

  public getLines(options?: Parameters<HttpClient['get']>[1]): Observable<IGetManyResponse<ILine>> {
    return this.http.get<IGetManyResponse<ILine>>('lines', {
      ...options,
      context: this.oeeTrackerHttpContext,
    });
  }

  public getCurrentAndPreviousShiftsOfLines(
    options?: Parameters<HttpClient['get']>[1],
  ): Observable<IGetManyResponse<ICurrentAndPreviousShifts>> {
    return this.http.get<IGetManyResponse<ICurrentAndPreviousShifts>>('lines/current-and-previous-shifts', {
      ...options,
      context: this.oeeTrackerHttpContext,
    });
  }
}
